package com.eladfinish.bible.contest.screens.search.ui.dialogs

import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SegmentedButton
import androidx.compose.material3.SegmentedButtonDefaults
import androidx.compose.material3.SingleChoiceSegmentedButtonRow
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDirection
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.close
import com.eladfinish.bible.contest.screens.search.mvi.SearchAction
import com.eladfinish.bible.contest.screens.search.suggestions.AutocompleteSuggestion
import com.eladfinish.bible.contest.screens.search.suggestions.PopularSearchesFilter
import com.eladfinish.bible.contest.search_history_empty
import com.eladfinish.bible.contest.search_popular_searches
import com.eladfinish.bible.contest.search_popular_searches_all_time
import com.eladfinish.bible.contest.search_popular_searches_hint
import com.eladfinish.bible.contest.search_popular_searches_mine
import com.eladfinish.bible.contest.search_popular_searches_title_format
import com.eladfinish.bible.contest.search_suggestions_this_month
import com.eladfinish.bible.contest.search_suggestions_this_week
import com.eladfinish.bible.contest.ui.components.dialogs.BasicDialogWithTitle
import com.eladfinish.bible.contest.ui.components.mouseVerticalScrollbar
import dev.icerock.moko.resources.StringResource
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.desc.desc
import kotlinx.collections.immutable.ImmutableList

const val POPULAR_SEARCH_ROW_TEST_TAG = "popular_search_row"

/**
 * The "most searched" list: the top 100 of all users, all time or in the recent window, or this
 * device's own. A tap runs the row, a long press only puts it in the field.
 */
@Composable
internal fun PopularSearchesDialog(
    filter: PopularSearchesFilter,
    entries: ImmutableList<AutocompleteSuggestion>,
    recentDays: Int,
    onAction: (SearchAction) -> Unit,
    onDismiss: () -> Unit,
) {
    BasicDialogWithTitle(
        onDismiss = onDismiss,
        title = if (entries.isEmpty()) {
            stringResource(SharedRes.strings.search_popular_searches)
        } else {
            stringResource(SharedRes.strings.search_popular_searches_title_format, entries.size)
        },
        onCancel = onDismiss,
        cancelText = SharedRes.strings.close.desc(),
        content = {
            FilterSelector(
                selected = filter,
                recentDays = recentDays,
                onSelected = { onAction(SearchAction.PopularSearchesFilterChanged(it)) },
            )

            if (entries.isEmpty()) {
                Text(
                    text = stringResource(SharedRes.strings.search_history_empty),
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(24.dp),
                    style = MaterialTheme.typography.bodyLarge,
                    textAlign = TextAlign.Center,
                )
                return@BasicDialogWithTitle
            }

            Text(
                text = stringResource(SharedRes.strings.search_popular_searches_hint),
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 4.dp),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            val listState = rememberLazyListState()

            LazyColumn(
                state = listState,
                modifier = Modifier.mouseVerticalScrollbar(listState),
            ) {
                itemsIndexed(
                    items = entries,
                    // a String: on Android a lazy-list key has to be saveable in a Bundle
                    key = { _, entry -> "${entry.origin}:${entry.query}" },
                ) { index, entry ->
                    PopularSearchRow(
                        number = index + 1,
                        entry = entry,
                        onClick = { onAction(SearchAction.PopularSearchClicked(entry)) },
                        onLongClick = { onAction(SearchAction.PopularSearchLongClicked(entry)) },
                    )
                    HorizontalDivider()
                }
            }
        },
    )
}

@Composable
private fun FilterSelector(
    selected: PopularSearchesFilter,
    recentDays: Int,
    onSelected: (PopularSearchesFilter) -> Unit,
) {
    val hotLabel: StringResource =
        if (recentDays <= 7) SharedRes.strings.search_suggestions_this_week else SharedRes.strings.search_suggestions_this_month

    val options = listOf(
        PopularSearchesFilter.ALL_TIME to SharedRes.strings.search_popular_searches_all_time,
        PopularSearchesFilter.HOT_NOW to hotLabel,
        PopularSearchesFilter.MINE to SharedRes.strings.search_popular_searches_mine,
    )

    SingleChoiceSegmentedButtonRow(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 8.dp),
    ) {
        options.forEachIndexed { index, (filter, label) ->
            SegmentedButton(
                selected = filter == selected,
                onClick = { onSelected(filter) },
                shape = SegmentedButtonDefaults.itemShape(index = index, count = options.size),
            ) {
                Text(
                    text = stringResource(label),
                    maxLines = 1,
                )
            }
        }
    }
}

@Composable
private fun PopularSearchRow(
    number: Int,
    entry: AutocompleteSuggestion,
    onClick: () -> Unit,
    onLongClick: () -> Unit,
) {
    val haptic = LocalHapticFeedback.current

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .combinedClickable(
                onClick = onClick,
                onLongClick = {
                    haptic.performHapticFeedback(HapticFeedbackType.LongPress)
                    onLongClick()
                },
            )
            .testTag(POPULAR_SEARCH_ROW_TEST_TAG)
            .padding(start = 16.dp, end = 16.dp, top = 6.dp, bottom = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = "$number.",
            modifier = Modifier.padding(end = 12.dp),
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )

        Column(
            modifier = Modifier.weight(1f),
        ) {
            Text(
                text = entry.query,
                modifier = Modifier.fillMaxWidth(),
                style = MaterialTheme.typography.titleMedium.copy(
                    textDirection = TextDirection.ContentOrRtl,
                ),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }

        // Isolated as left-to-right so the digits read the same inside a right-to-left row.
        Text(
            text = "⁦${entry.count}⁩",
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}
